import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { useMemo } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { createUseStyles } from 'react-jss';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { HeatMapSexFilterOption } from '../../map-data/map-data-types';
import type { HeatMapLegend } from '../../map-data/heatmap-legend-config';
import type { LcpnTheme } from '../../styles/theme';
import { Typography } from '../Typography';

import { KeyColor } from './KeyColor';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  legend: {
    position: 'absolute',
    bottom: 38,
    right: 8,
    background: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 12.5,
    minWidth: 250,
    padding: theme.spacing(3),
  },

  heading: {
    display: 'flex',
    padding: theme.spacing(1),
    borderBottom: `solid 1px ${theme.colors['grey-100']}`,
    width: '100%',
  },

  description: {
    textAlign: 'center',
    paddingTop: theme.spacing(2),
    width: '100%',
    maxWidth: '12em;',
    margin: '0 auto',
  },

  data: {
    display: 'flex',
    gap: theme.spacing(4),
  },

  filterType: {
    display: 'flex',
  },

  values: {
    display: 'flex',
    flexDirection: 'column',
  },

  item: {
    display: 'flex',
    gap: theme.spacing(2),
    fontSize: theme.fontSizes.p2,
  },
}));

type Props = {
  legendConfig: HeatMapLegend;
  title: string;
  description: ReactNode;
  filterType: HeatMapSexFilterOption;
  setFilterType: Dispatch<SetStateAction<HeatMapSexFilterOption>>;
};

export const HeatmapLegend: FC<Props> = ({
  legendConfig,
  filterType,
  setFilterType,
  description,
}) => {
  const rows = useMemo(() => Object.values(legendConfig), [legendConfig]);
  const classes = useStyles();
  const { t } = useTranslation();

  const handleSexChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: HeatMapSexFilterOption | null,
  ) => {
    if (newValue !== null) {
      setFilterType(newValue);
    }
  };

  return (
    <div className={classes.legend}>
      <div className={classes.data}>
        <div className={classes.filterType}>
          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={handleSexChange}
            orientation="vertical"
            aria-label="sex filter selection"
            size="small"
            sx={{
              height: '100%',
              gap: '1px',
              '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                marginTop: '0 !important',
              },
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: 1,
                px: 1.5,
                py: 0,
                fontSize: '0.75rem',
                fontWeight: 500,
                minWidth: 'auto',
                justifyContent: 'flex-start',
                flex: '1 1 33.333%',
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
              '& .MuiToggleButton-root:not(.Mui-selected)': {
                backgroundColor: '#ffffff',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              },
            }}
          >
            <ToggleButton value="Combined" aria-label="Both sexes">
              {t('heatMap.genders.bothSexes')}
            </ToggleButton>
            <ToggleButton value="Male" aria-label="Male">
              {t('heatMap.genders.male')}
            </ToggleButton>
            <ToggleButton value="Female" aria-label="Female">
              {t('heatMap.genders.female')}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div className={classes.values}>
          {rows.map((row, index) => (
            <div key={index} className={classes.item}>
              <KeyColor color={row.color} />
              <span>
                {row.label === 'No data' ? t('nccpLegend.noData') : row.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className={classes.description}>
        <Typography variant="p3" preserveReactNodes>
          {description}
        </Typography>
      </div>
    </div>
  );
};
